import request from '@/utils/request';
import {Session} from '@/utils/storage';
import {validateNull} from '@/utils/validate';
import {useUserStore} from '@/stores/user';
import other from '@/utils/other';

// OAuth form 格式要求
const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded';

// 登录方式枚举（JS 写法）
export const LoginTypeEnum = {
    PASSWORD: 0,
    MOBILE: 1,
    REGISTER: 2,
    EXPIRE: 3,
    FORGET: 4,
};

// 社交登录方式
export const SocialLoginEnum = {
    SMS: 'SMS',
    APP_SMS: 'APP-SMS',
};

/**
 * 获取启用的区号列表（供下拉选择）
 */
export const fetchDialCodeList = () => {
    return request({
        url: '/admin/dialCodeConfig/list',
        method: 'get',
    });
};

// 登录
export const login = (data) => {
    const basicAuth = 'Basic ' + window.btoa(import.meta.env.VITE_OAUTH2_APP_CLIENT);
    Session.set('basicAuth', basicAuth);

    // 密码加密
    const encPassword = other.encryption(
        data.password,
        import.meta.env.VITE_PWD_ENC_KEY
    );

    const {username, grant_type, scope, code, randomStr, registerToken, loginType, phoneCode,flag} = data;

    return request({
        url: '/auth/oauth2/token',
        method: 'post',
        params: {username, grant_type, scope, code, randomStr, registerToken, loginType, phoneCode,flag},
        data: {password: encPassword},
        headers: {
            skipToken: true,
            Authorization: basicAuth,
            'Content-Type': FORM_CONTENT_TYPE,
            'Enc-Flag': 'false',
        },
    });
};

// 手机号验证码登录
export const loginByMobile = (mobile, code) => {
    const grant_type = 'mobile';
    const scope = 'server';

    const basicAuth =
        'Basic ' + window.btoa(import.meta.env.VITE_OAUTH2_MOBILE_CLIENT);
    Session.set('basicAuth', basicAuth);

    return request({
        url: '/auth/oauth2/token',
        headers: {
            skipToken: true,
            Authorization: basicAuth,
            'Content-Type': FORM_CONTENT_TYPE,
        },
        method: 'post',
        params: {
            mobile: `${SocialLoginEnum.SMS}@${mobile}`,
            code,
            grant_type,
            scope,
        },
    });
};

// 社交登录
export const loginBySocial = (state, code) => {
    const grant_type = 'mobile';
    const scope = 'server';

    const basicAuth =
        'Basic ' + window.btoa(import.meta.env.VITE_OAUTH2_SOCIAL_CLIENT);
    Session.set('basicAuth', basicAuth);

    return request({
        url: '/auth/oauth2/token',
        headers: {
            skipToken: true,
            skipTenant: true,
            Authorization: basicAuth,
            'Content-Type': FORM_CONTENT_TYPE,
        },
        method: 'post',
        params: {
            mobile: `${state}@${code}`,
            code,
            grant_type,
            scope,
        },
    });
};

// 发送短信验证码
export const sendMobileCode = (mobile) => {
    return request({
        url: '/app/sysMessage/send/smsCode',
        method: 'get',
        params: {mobile},
    });
};

// 刷新 token
export const refreshTokenApi = (refresh_token) => {
    const grant_type = 'refresh_token';
    const scope = 'server';
    const basicAuth = Session.get('basicAuth');

    return request({
        url: '/auth/oauth2/token',
        headers: {
            skipToken: true,
            skipTenant: true,
            Authorization: basicAuth,
            'Content-Type': FORM_CONTENT_TYPE,
            'CLIENT-TOC': 'Y',
            'X-Device-Type': document.querySelector('html')?.getAttribute('device') || 'desktop',
        },
        method: 'post',
        params: {refresh_token, grant_type, scope},
    });
};

// 校验 token 过期并自动续期
export const checkToken = (refreshTime, refreshLock) => {
    const basicAuth = Session.get('basicAuth');

    request({
        url: '/auth/token/check_token',
        headers: {
            skipToken: true,
            skipTenant: true,
            Authorization: basicAuth,
            'Content-Type': FORM_CONTENT_TYPE,
        },
        method: 'get',
        params: {token: Session.getToken()},
    })
        .then((response) => {
            if (validateNull(response) || response.code === 1) {
                clearInterval(refreshTime);
                return;
            }

            // 密码过期检查：check_token 返回 password_expired=true 时触发弹窗
            if (response.data && response.data.password_expired === true) {
                if (window.__PASSWORD_EXPIRED_CALLBACK && !window.__PASSWORD_EXPIRED_TRIGGERED) {
                    window.__PASSWORD_EXPIRED_TRIGGERED = true;
                    window.__PASSWORD_EXPIRED_CALLBACK();
                }
            }

            const expire = Date.parse(response.data.expiresAt);
            if (expire) {
                const expiredPeriod = expire - new Date().getTime();

                // 小于 30 分钟自动续约
                if (expiredPeriod <= 30 * 60 * 1000) {
                    if (!refreshLock) {
                        refreshLock = true;

                        useUserStore()
                            .refreshToken()
                            .catch(() => clearInterval(refreshTime));

                        refreshLock = false;
                    }
                }
            }
        })
        .catch(() => {
            clearInterval(refreshTime);
        });
};

// 获取用户信息
export const getUserInfo = () => {
    return request({
        url: '/app/appuser/front/info',
        method: 'get',
    });
};

// 退出登录
export const logout = () => {
    return request({
        url: '/auth/token/logout',
        method: 'delete',
    });
};

/**
 * 根据访问域名查询对应的租户ID（无需认证）
 * @param {string} domain - 当前访问域名，如 player.example.com
 * @returns {Promise} data 字段为租户ID Long，未匹配时为 null
 */
export const fetchTenantByDomain = (domain) => {
    return request({
        url: '/admin/domainManage/tenantByDomain',
        method: 'get',
        params: { domain },
        headers: {
            skipToken: true,
            skipTenant: true,
        },
    });
};

/**
 * 获取图形验证码URL
 * @param {string} randomStr - 随机字符串
 * @param {string} type - 验证码类型: 'image' 算术题, 'number' 6位数字
 * @returns {string} 验证码图片URL
 */
export const getImageCodeUrl = (randomStr, type = 'image') => {
    const endpoint = type === 'number' ? 'number' : 'image';
    return `${import.meta.env.VITE_API_URL}/auth/code/${endpoint}?randomStr=${randomStr}`;
};
